import { Button } from '@/components'
import type { TimesheetsModuleConfig } from '@/modules/companies'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import { useFormWatch } from '@/utils'
import {
  useBreadcrumbs,
  useModuleConfig,
  useNodes,
  usePermissions,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addDays, formatISO, startOfDay } from 'date-fns'
import { useEffect } from 'react'
import { useWatch } from 'react-hook-form'
import { useLocation } from 'react-router'
import { useTimeEntryForm } from '../hooks'
import { useTimeEntryMutations } from '../mutations.hooks'
import { USER_TIME_ENTRIES_QUERY } from '../queries'
import {
  exceedsDailyWorkLimit,
  getTimeEntryFormSpans,
} from '../time-entry-form.schema'
import type { TimeEntryFormValues } from '../types'
import type { TimesheetsSalaryWorkTime } from '../types.graphql'
import { TimeEntryForm } from './time-entry-form'

interface Props {
  salaryWorkTime: TimesheetsSalaryWorkTime
}

export const TimeEntryFormAPI: React.FC<Props> = ({ salaryWorkTime }) => {
  const translations = useTranslate({
    newTimeEntry: 'timesheets.new-time-entry',
    submit: 'common.submit',
  })
  const { getModuleOptionStatus } = useModuleConfig<TimesheetsModuleConfig>(
    Module.TIMESHEETS
  )
  const { setOverrides, overrides } = useBreadcrumbs()
  const { hasPermissions } = usePermissions()

  const location = useLocation()
  const form = useTimeEntryForm(
    null,
    salaryWorkTime,
    location.state?.initialUser
  )
  const { createTimeEntries, loading } = useTimeEntryMutations()

  const [userId, start, end, pauses] = useWatch({
    control: form.control,
    name: ['userId', 'start', 'end', 'pauses'],
  })
  const overtimeSalaryCodeId = useFormWatch(form, 'overtimeSalaryCodeId')

  const day = startOfDay(start)
  const { data } = useQuery(USER_TIME_ENTRIES_QUERY, {
    variables: {
      user: userId,
      datetimeStartGt: day.toISOString(),
      datetimeStartLt: addDays(day, 1).toISOString(),
    },
    skip: !userId,
  })
  const existingTimeEntries = useNodes(data?.allTimeEntries)

  const overtimeRequired =
    getModuleOptionStatus(ModuleOptionsValue.TIMESHEETS_OVERTIME_OPTIONS) &&
    exceedsDailyWorkLimit(
      getTimeEntryFormSpans({ start, end, pauses }),
      existingTimeEntries,
      salaryWorkTime.dailyWorkLimit
    )

  const canSetSalaryCodeManually = hasPermissions(
    PERMISSIONS.timesheets.overrideTimeEntrySalaryCode,
    false
  )

  const submitDisabled =
    !form.formState.isValid ||
    (overtimeRequired && !overtimeSalaryCodeId) ||
    loading

  async function handleSubmit(values: TimeEntryFormValues) {
    const entries = getTimeEntryFormSpans(values)

    await createTimeEntries(
      entries.map((entry, idx) => ({
        user: values.userId,
        vehicle: values.vehicleId || null,
        datetimeStart: formatISO(entry.start),
        datetimeEnd: formatISO(entry.end),
        facility: values.facility || null,
        comment: values.comment,
        salaryCodeOvertime: values.overtimeSalaryCodeId || null,
        salaryCodeOrdinary: values.salaryCodeId || null,
        diet: values.diet && idx === 0,
        department: values.department || null,
        // An explicit zero; a missing pause is filled in from the salary work time
        pause: 0,
      }))
    )
  }

  useEffect(() => {
    if (overrides['register']) return
    setOverrides({ register: translations.newTimeEntry })
  }, [overrides, setOverrides, translations.newTimeEntry])

  return (
    <form
      className="space-y-6"
      onSubmit={form.handleSubmit(handleSubmit)}
      noValidate
    >
      <TimeEntryForm
        form={form}
        salaryWorkTime={salaryWorkTime}
        canSetSalaryCodeManually={canSetSalaryCodeManually}
        overtimeRequired={overtimeRequired}
        allowPause
      />

      <div className="flex justify-end">
        <Button
          type="submit"
          className="w-full md:w-auto md:min-w-50"
          disabled={submitDisabled}
        >
          {translations.submit}
        </Button>
      </div>
    </form>
  )
}
